import { CELL, GROUND } from '../core/config';
import {
  createArchetype,
  createProp,
  key,
  MATERIALS,
  type Building,
  type Cell,
  type City,
  type Place,
} from './city';
import { furnish } from './furniture';
import type { Vec } from '../destruction/structure';

export interface CoastLayout {
  kiosks: number[];
  paths: { id: string; points: Vec[]; width: number }[];
  guards: { id: string; spawn: Vec; places: Place[] }[];
  beach: { x: number; z: number; length: number; depth: number };
}
const nextId = (city: City) =>
  Math.max(
    ...[
      ...city.buildings,
      ...(city.props ?? []),
      ...(city.bridges ?? []),
      ...(city.infrastructure ?? []),
    ].map((b) => b.id),
  ) + 1;

/** Four real occupied shop parcels face the public coastal street. Interior and
 * stock/staff authoring subsequently treats them as normal operating shops. */
export function prepareCoastParcels(city: City): City {
  if (!city.regional || city.coast) return city;
  const coast: CoastLayout = (city.coast = {
    kiosks: [],
    paths: [],
    guards: [],
    beach: { x: 180, z: 630, length: 420, depth: 38 },
  });
  let id = nextId(city);
  for (const [i, x] of [220, 310, 400, 490].entries()) {
    const b = createArchetype(
      id++,
      x,
      619,
      i === 2 ? 'market-hall' : 'corner-shop',
      [10, 8, 12, 9][i],
      [10, 12, 8, 10][i],
      1,
      [0xd5ccb8, 0xc2cabc, 0xd4b9a2, 0xc9d0c3][i],
      0x648b87,
    );
    b.name = ['Promenade cafe', 'Linden beach provisions', 'Shoreline food market', 'Seabreeze corner shop'][
      i
    ];
    b.district = 'coast';
    city.buildings.push(b);
    coast.kiosks.push(b.id);
  }
  return city;
}

/** Public footpaths consist of unique damageable paving cells, including their
 * intersections. Seats and rescue fixtures are attached to actual floor cells. */
export function prepareCoastAmenities(city: City): City {
  const coast = city.coast;
  if (!coast || coast.paths.length) return city;
  city.props ??= [];
  city.places ??= [];
  let id = nextId(city);
  const tiles = new Map<string, Building>(),
    cells = new Map<string, number>();
  const pave = (x: number, z: number, w: number, d: number) => {
    for (let gz = Math.round(z / CELL); gz < Math.round((z + d) / CELL); gz++)
      for (let gx = Math.round(x / CELL); gx < Math.round((x + w) / CELL); gx++) {
        const tag = `${gx},${gz}`;
        if (cells.has(tag)) continue;
        const tx = Math.floor(gx / 30),
          tz = Math.floor(gz / 30),
          chunk = `${tx},${tz}`;
        let b = tiles.get(chunk);
        if (!b) {
          b = {
            id: id++,
            name: 'Linden promenade paving',
            kind: 'paving',
            category: 'prop',
            x: tx * 30 * CELL,
            z: tz * 30 * CELL,
            w: 30,
            d: 30,
            h: 1,
            baseY: GROUND,
            color: 0xc7c5ac,
            accent: 0x849687,
            district: 'coast',
            cells: [],
          };
          tiles.set(chunk, b);
          city.props!.push(b);
        }
        const xx = gx - tx * 30,
          zz = gz - tz * 30,
          cell = b.id * 1e6 + key(xx, 0, zz);
        b.cells.push({
          id: cell,
          x: xx,
          y: 0,
          z: zz,
          material: 'concrete',
          role: 'slab',
          hp: 2.6,
          color: (gx + gz) % 4 === 0 ? 0xb9baa3 : 0xcbc9b0,
          bounds: { x: 0, y: 0, z: 0, w: 1, h: 0.12 / CELL, d: 1 },
        });
        cells.set(tag, cell);
      }
  };
  const support = (x: number, z: number) => cells.get(`${Math.floor(x / CELL)},${Math.floor(z / CELL)}`);
  const path = (points: Vec[], width = 3.75) =>
    coast.paths.push({ id: `coast-path:${coast.paths.length}`, points, width });
  const p = (x: number, z: number): Vec => ({ x, y: GROUND + 0.12, z });
  pave(180, 636.25, 420, 6.25);
  path([p(180.625, 639.375), p(599.375, 639.375)], 6.25);
  for (const x of [202.5, 262.5, 352.5, 442.5, 532.5, 592.5]) {
    pave(x - 1.25, 615, 3.75, 21.25);
    path([{ x, y: GROUND + 0.32, z: 614.5 }, p(x, 616.25), p(x, 639.375)]);
  }
  const fixture = (
    x: number,
    z: number,
    furniture: NonNullable<Building['furniture']>,
    variant: number,
    turn = false,
  ) => {
    const item: Building = {
      id: id++,
      name: `Promenade ${furniture}`,
      kind: 'furniture',
      furniture,
      category: 'prop',
      x,
      z,
      w: 1,
      d: 1,
      h: 2,
      baseY: GROUND + 0.12,
      color: 0xbca283,
      accent: 0x73877e,
      district: 'coast',
      parentCell: support(x + 0.625, z + 0.625),
      cells: [],
    };
    const cell: Cell = {
      id: item.id * 1e6,
      x: 0,
      y: 0,
      z: 0,
      material: 'wood' as const,
      role: 'prop' as const,
      hp: MATERIALS.wood.strength,
      color: item.color,
    };
    furnish(cell, furniture, variant);
    if (turn) {
      for (const piece of cell.parts ?? []) {
        piece.x = 1 - piece.x - piece.w;
        piece.z = 1 - piece.z - piece.d;
      }
      const b = cell.bounds!;
      b.x = 1 - b.x - b.w;
      b.z = 1 - b.z - b.d;
    }
    item.cells.push(cell);
    city.props!.push(item);
    return item;
  };
  for (let i = 0; i < 22; i++) {
    const originalX = 187.5 + i * 18.75,
      x =
        (originalX >= 375 && originalX < 383) || (originalX >= 540 && originalX < 548)
          ? originalX + 10
          : originalX;
    pave(x - 1.25, 642.5, 5, 7.5);
    path([p(x - 0.625, 639.375), p(x - 0.625, 648.125), p(x + 0.625, 648.125)]);
    for (let j = 0; j < 2; j++) {
      const chair = fixture(x + j * 1.125, 646.25, 'bench', i, true),
        px = chair.x + 0.625,
        z = 648.125;
      city.places.push({
        id: `coast-seat:${i}:${j}`,
        name: 'Sea-facing promenade seat',
        kind: 'seat',
        x: px,
        y: GROUND + 0.12,
        z,
        floor: 0,
        capacity: 1,
        duration: 20 + (i % 3) * 4,
        furnitureId: chair.id,
        supportCell: support(px, z),
        pose: {
          x: px,
          y: GROUND + 0.12,
          z: chair.z + 0.68,
          yaw: Math.PI,
          posture: 'seated',
          seatHeight: 0.49,
          duration: 1.1,
        },
      });
    }
  }
  for (let i = 0; i < 10; i++) {
    const x = 211.25 + i * 38.75;
    pave(x - 1.25, 642.5, 3.75, 20);
    path([p(x, 639.375), p(x, 660)]);
    city.places.push({
      id: `coast-watch:${i}`,
      name: 'Shore observation point',
      kind: 'watch',
      x,
      y: GROUND + 0.12,
      z: 660,
      floor: 0,
      capacity: 1,
      duration: 14 + (i % 4) * 3,
      facing: Math.PI,
      supportCell: support(x, 660),
    });
  }
  for (let i = 0; i < 4; i++) {
    const x = 248.75 + i * 90;
    pave(x - 2.5, 645, 5, 7.5);
    path([p(x, 639.375), p(x, 649.375)]);
    // A short paved spur joins the exercise pad to the main route.
    pave(x - 1.25, 642.5, 3.75, 2.5);
    city.places.push({
      id: `coast-exercise:${i}`,
      name: 'Promenade stretching space',
      kind: 'exercise',
      x,
      y: GROUND + 0.12,
      z: 649.375,
      floor: 0,
      capacity: 1,
      duration: 16,
      facing: Math.PI,
      supportCell: support(x, 649.375),
    });
  }
  for (const [i, x] of [375, 540].entries()) {
    pave(x, 642.5, 10, 21.25);
    path([p(x + 4.375, 639.375), p(x + 4.375, 661.875), p(x + 3.125, 661.875)]);
    const shade = createProp(id++, x, 646.25, 'shelter', i);
    shade.name = `Lifeguard post ${i + 1}`;
    shade.district = 'coast';
    shade.baseY = GROUND + 0.12;
    shade.parentCell = support(x + 0.625, 646.875);
    for (const c of shade.cells) if (c.role === 'roof') c.color = 0xd2c9a9;
    city.props.push(shade);
    const signId = id++,
      signCell: Cell = {
        id: signId * 1e6,
        x: 0,
        y: 0,
        z: 0,
        material: 'metal',
        role: 'prop',
        hp: 2.8,
        color: 0xb46750,
        bounds: { x: 0, y: 0, z: 0, w: 1.5 / CELL, h: 0.55 / CELL, d: 0.1 / CELL },
        parts: [
          { x: 0, y: 0, z: 0, w: 1.5 / CELL, h: 0.55 / CELL, d: 0.07 / CELL, color: 0xb46750 },
          {
            x: 0.66 / CELL,
            y: 0.07 / CELL,
            z: 0.07 / CELL,
            w: 0.16 / CELL,
            h: 0.41 / CELL,
            d: 0.025 / CELL,
            color: 0xf0e8d0,
          },
          {
            x: 0.48 / CELL,
            y: 0.2 / CELL,
            z: 0.07 / CELL,
            w: 0.52 / CELL,
            h: 0.15 / CELL,
            d: 0.025 / CELL,
            color: 0xf0e8d0,
          },
        ],
      };
    city.props.push({
      id: signId,
      name: 'Lifeguard rescue sign',
      kind: 'furniture',
      furniture: 'partition',
      category: 'prop',
      district: 'coast',
      x: x + 2.375,
      z: 651.27,
      baseY: GROUND + 0.12 + 3 * CELL - 0.46,
      w: 2,
      d: 1,
      h: 1,
      color: 0xb46750,
      accent: 0xf0e8d0,
      parentCell: shade.cells.find((c) => c.x === 2 && c.y === 3 && c.z === 3)!.id,
      cells: [signCell],
    });
    const item = fixture(x + 1.875, 648.75, 'cabinet', i);
    item.name = 'Lifeguard rescue-equipment locker';
    const equipment: Place = {
        id: `coast-guard:${i}:equipment`,
        name: 'Rescue equipment and first-aid check',
        kind: 'staff',
        x: x + 2.5,
        y: GROUND + 0.12,
        z: 648.125,
        floor: 0,
        capacity: 1,
        duration: 10,
        furnitureId: item.id,
        supportCell: support(x + 2.5, 648.125),
      },
      watch: Place = {
        id: `coast-guard:${i}:watch`,
        name: 'Lifeguard water-safety observation',
        kind: 'staff',
        x: x + 3.125,
        y: GROUND + 0.12,
        z: 661.875,
        floor: 0,
        capacity: 1,
        duration: 22,
        facing: Math.PI,
        supportCell: support(x + 3.125, 661.875),
      };
    city.places.push(equipment, watch);
    coast.guards.push({ id: `coast-guard:${i}`, spawn: p(x + 3.125, 643.125), places: [equipment, watch] });
  }
  for (let i = 0; i < 12; i++) {
    const x = 185 + i * 35,
      light = createProp(id++, x, 632.5, 'lamp', i);
    light.name = 'Promenade light';
    light.district = 'coast';
    city.props.push(light);
  }
  const district = city.districts?.find((d) => d.id === 'coast');
  if (district) {
    district.x = 385;
    district.z = 625;
  }
  return city;
}
